/**
 * React Query hooks for intake (ATLAS-100, U35).
 *
 * `/me` is asked once per page life and decides everything else: for someone
 * on no intake list the browser sends `/me` and nothing more (D-112). The
 * intake role comes from here and nowhere else, never from Entra roles
 * (ADR 0008).
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData, type QueryKey } from '@tanstack/react-query';
import type {
  IntakeBoardColumn,
  IntakeColumnRead,
  IntakeFeedRead,
  IntakeCheckMarkInput,
  IntakeDecisionInput,
  IntakeEditInput,
  IntakeItemDetail,
  IntakeMakePrivateResult,
  IntakeMe,
  IntakeNeedResult,
  IntakePriorityInput,
  IntakeReviewCycle,
  IntakeReviewCycleInput,
  IntakeRole,
  IntakeSenderEditInput,
  IntakeSensitiveResolveInput,
  IntakeTeamView,
  IntakeWeeklyPass,
} from '@jmtest/shared-types';
import { useAuth } from '@/auth/useAuth';
import { clearActionError, reportActionError } from '@/components/ActionErrorBanner';
import {
  addTeamMember,
  answerReviewerQuestion,
  askSender,
  confirmRoute,
  createReviewCycle,
  decideItem,
  editItem,
  editOwnIdea,
  fetchFeed,
  fetchIntakeColumn,
  fetchIntakeItem,
  fetchIntakeMe,
  fetchTeam,
  fetchWeeklyPass,
  GRAPH_TOKEN_UNAVAILABLE,
  INTAKE_ALREADY_PRIVATE,
  IntakeApiError,
  makePrivate,
  markChecked,
  removeTeamMember,
  resolvePrivate,
  setFollow,
  setPriority,
  unvoteNeed,
  voteNeed,
  withdrawOwnIdea,
  type IntakeFeedParams,
  type IntakeTeamListPath,
} from '@/api/intake';

export const intakeKeys = {
  all: ['intake'] as const,
  me: ['intake', 'me'] as const,
  team: ['intake', 'team'] as const,
  column: (column: IntakeBoardColumn, mine = false) => ['intake', 'column', column, { mine }] as const,
  item: (code: string) => ['intake', 'item', code] as const,
};

/**
 * Reads retry once on a 5xx or network failure and never on a 4xx (plan Q6).
 * Overrides the app-wide rule in `App.tsx`, which decides by message text.
 */
export function intakeReadRetry(failureCount: number, error: unknown): boolean {
  if (!(error instanceof IntakeApiError)) return false;
  const transient = error.status === 0 || error.status >= 500;
  return transient && failureCount < 1;
}

/**
 * The caller's intake role. Cached for the life of the page: a reload asks
 * again, so someone added on the team page gets in on their next reload
 * (plan Q4). Set explicitly rather than left to React Query's defaults
 * (U35 criterion 8). No retry: a failure means "unavailable" for this load
 * (criterion 7).
 */
export function useIntakeMe() {
  const { isAuthenticated } = useAuth();
  return useQuery<IntakeMe, IntakeApiError>({
    queryKey: intakeKeys.me,
    queryFn: fetchIntakeMe,
    enabled: isAuthenticated,
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}

export type IntakeAccessState = 'loading' | 'none' | 'ready' | 'unavailable' | 'signInExpired';

export interface IntakeAccess {
  state: IntakeAccessState;
  /** Set only when `state` is `ready`. */
  role: IntakeRole | null;
  onSponsorList: boolean;
}

/**
 * `/me` reduced to what a page needs: still loading, no intake for this
 * person (D-112), sign-in expired (a 401, ATLAS-100b), intake unavailable
 * (any other error, D-113), or ready with a role.
 */
export function useIntakeAccess(): IntakeAccess {
  const me = useIntakeMe();
  if (me.isError) {
    const state = me.error?.status === 401 ? 'signInExpired' : 'unavailable';
    return { state, role: null, onSponsorList: false };
  }
  if (!me.data) return { state: 'loading', role: null, onSponsorList: false };
  if (me.data.role === null) return { state: 'none', role: null, onSponsorList: false };
  return { state: 'ready', role: me.data.role, onSponsorList: me.data.onSponsorList };
}

/** One board column. Sent only once `/me` says the caller has intake. */
export function useIntakeColumn(column: IntakeBoardColumn, { mine = false }: { mine?: boolean } = {}) {
  const access = useIntakeAccess();
  return useQuery<IntakeColumnRead, IntakeApiError>({
    queryKey: intakeKeys.column(column, mine),
    queryFn: () => fetchIntakeColumn(column, { mine }),
    enabled: access.state === 'ready',
    retry: intakeReadRetry,
  });
}

/** One item. A 404 means it doesn't exist or the caller can't see it (D-58). */
export function useIntakeItem(code: string | undefined) {
  const access = useIntakeAccess();
  return useQuery<IntakeItemDetail, IntakeApiError>({
    queryKey: intakeKeys.item(code ?? ''),
    queryFn: () => fetchIntakeItem(code ?? ''),
    enabled: access.state === 'ready' && Boolean(code),
    retry: intakeReadRetry,
  });
}

/**
 * Every intake write. No optimistic update, so a failed write leaves the page
 * as it was; the failure shows in the gate's action error banner with the
 * server's sentence for a 4xx (criterion 4). No retry. On success the banner
 * clears and only the given keys refetch.
 *
 * `reportErrors: false` keeps the failure off the shared banner, for a page
 * that shows it where the person acted instead (the team page, ATLAS-106b).
 * The default is unchanged.
 */
export function useIntakeMutation<TVariables, TData>(
  fn: (variables: TVariables) => Promise<TData>,
  { invalidate = [], reportErrors = true }: { invalidate?: QueryKey[]; reportErrors?: boolean } = {},
) {
  const queryClient = useQueryClient();
  return useMutation<TData, Error, TVariables>({
    mutationFn: fn,
    retry: false,
    onError: (error) => {
      if (reportErrors) reportActionError(error);
    },
    onSuccess: async () => {
      clearActionError();
      await Promise.all(invalidate.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
    },
  });
}

// ── The review team (ATLAS-106, U41) ─────────────────────────────────────────

export const TEAM_DIRECTORY_MESSAGE = "Couldn't reach the company directory. Try again in a minute.";
export const TEAM_PERSON_NOT_FOUND_MESSAGE = "That person isn't in the company directory, or their account is disabled.";
export const TEAM_LOST_ACCESS_MESSAGE = "You can't change the review team any more. Reload the page to see what you can do.";

export type TeamWrite = 'add' | 'remove';

export type TeamWriteOutcome = { kind: 'done' } | { kind: 'lost-access'; message: string } | { kind: 'error'; message: string };

/**
 * What a failed team write means (plan "The retry rule", U41 criterion 9). A
 * write can arrive twice: a timeout, a double tap, a second admin. The
 * answer that says it already happened counts as done. The match is on the
 * error code, never on status alone: a 404 is either "not on the list"
 * (done) or the gate's "not found" (the caller lost access).
 */
export function teamWriteOutcome(error: unknown, write: TeamWrite): TeamWriteOutcome {
  if (!(error instanceof IntakeApiError)) {
    return { kind: 'error', message: error instanceof Error && error.message ? error.message : "That didn't work. Try again." };
  }
  if (write === 'add' && error.code === 'INTAKE_ALREADY_ON_LIST') return { kind: 'done' };
  if (write === 'remove' && error.code === 'INTAKE_NOT_ON_LIST') return { kind: 'done' };
  if (error.code === 'NOT_FOUND') return { kind: 'lost-access', message: TEAM_LOST_ACCESS_MESSAGE };
  if (error.code === 'INTAKE_PERSON_NOT_FOUND') return { kind: 'error', message: TEAM_PERSON_NOT_FOUND_MESSAGE };
  if (error.code === 'GRAPH_UNAVAILABLE' || error.code === GRAPH_TOKEN_UNAVAILABLE || (error.status === 400 && error.code === 'BAD_REQUEST')) {
    return { kind: 'error', message: TEAM_DIRECTORY_MESSAGE };
  }
  return { kind: 'error', message: error.message };
}

/** The whole review team and its change log. Every employee may read it (D-67). */
export function useTeam() {
  const access = useIntakeAccess();
  return useQuery<IntakeTeamView, IntakeApiError>({
    queryKey: intakeKeys.team,
    queryFn: fetchTeam,
    enabled: access.state === 'ready',
    retry: intakeReadRetry,
  });
}

/**
 * One team write. A success answers the whole team view, which replaces the
 * cache; a write that had already happened is treated as done. Either way the
 * team is read again (plan Q5: the team only, since no admin's change alters
 * their own role). Lost access reads `/me` again, so the gate can act on it.
 */
function useTeamWrite<TVariables>(write: TeamWrite, send: (variables: TVariables) => Promise<IntakeTeamView>) {
  const queryClient = useQueryClient();
  return useIntakeMutation<TVariables, IntakeTeamView | null>(
    async (variables) => {
      try {
        const view = await send(variables);
        queryClient.setQueryData(intakeKeys.team, view);
        return view;
      } catch (error) {
        const outcome = teamWriteOutcome(error, write);
        if (outcome.kind === 'done') return null;
        if (outcome.kind === 'lost-access') void queryClient.invalidateQueries({ queryKey: intakeKeys.me });
        const status = error instanceof IntakeApiError ? error.status : 0;
        const code = error instanceof IntakeApiError ? error.code : null;
        throw new IntakeApiError(status, outcome.message, code);
      }
    },
    // The team page shows a failure where the person acted, never in the shared banner (ATLAS-106b).
    { invalidate: [intakeKeys.team], reportErrors: false },
  );
}

export function useAddTeamMember() {
  return useTeamWrite('add', ({ list, personOid }: { list: IntakeTeamListPath; personOid: string }) => addTeamMember(list, personOid));
}

export function useRemoveTeamMember() {
  return useTeamWrite('remove', ({ list, oid }: { list: IntakeTeamListPath; oid: string }) => removeTeamMember(list, oid));
}

// ── The ideas feed and "I need this too" (ATLAS-101, U36) ────────────────────

/** Every feed key starts with `intakeKeys.all`, so invalidating intake reaches the feed too. */
export const feedKeys = {
  all: ['intake', 'feed'] as const,
  list: ({ sort, mine = false, status }: Omit<IntakeFeedParams, 'cursor'>) => ['intake', 'feed', { sort, mine, status: status ?? null }] as const,
};

/** The feed's 400 for a page more than an hour old: start again from page 1 (ATLAS-87). */
export const FEED_CURSOR_EXPIRED = 'FEED_CURSOR_EXPIRED';

/**
 * One tab and stage of the feed, a page at a time. "Show more" sends the last
 * page's `nextCursor`; a null cursor means there is no more. Sent only once
 * `/me` says the caller has intake (D-112).
 */
export function useIntakeFeed(params: Omit<IntakeFeedParams, 'cursor'>) {
  const access = useIntakeAccess();
  return useInfiniteQuery<IntakeFeedRead, IntakeApiError, InfiniteData<IntakeFeedRead, string | null>, ReturnType<typeof feedKeys.list>, string | null>({
    queryKey: feedKeys.list(params),
    queryFn: ({ pageParam }) => fetchFeed({ ...params, cursor: pageParam }),
    initialPageParam: null,
    getNextPageParam: (last) => last.nextCursor,
    enabled: access.state === 'ready',
    retry: intakeReadRetry,
  });
}

export const VOTE_FAILED_MESSAGE = "Couldn't save your vote. Try again.";
export const VOTE_UNCONFIRMED_MESSAGE = "We couldn't confirm your vote was saved. The count updates when the list reloads.";
export const UNVOTE_UNCONFIRMED_MESSAGE = "We couldn't confirm your vote was removed. The count updates when the list reloads.";

export const VOTE_REFUSED_OWN_MESSAGE = "You can't vote on your own idea.";
export const VOTE_REFUSED_CLOSED_MESSAGE = 'This idea is closed to new votes.';
export const VOTE_REFUSED_GONE_MESSAGE = "This idea isn't open for votes any more.";

/** The API's vote refusals (ATLAS-87, `votes.service.ts`), in the board's words (ATLAS-101e, D-303). */
const VOTE_REFUSALS: ReadonlyMap<string, string> = new Map([
  ['NEED_OWN_ITEM', VOTE_REFUSED_OWN_MESSAGE],
  ['NEED_CLOSED_ITEM', VOTE_REFUSED_CLOSED_MESSAGE],
  ['NEED_PRIVATE_ITEM', VOTE_REFUSED_GONE_MESSAGE],
]);

/**
 * What a failed vote means to the person. A refusal (409: the idea is yours,
 * closed or private; 404: it went out of sight) says why, in the board's
 * words, and the card is read again so it shows what's true (ATLAS-101e
 * criteria 4 to 7). A 409 with any other code stays quiet and is read again,
 * as before (D-350). A timeout or lost connection may have saved the vote, so
 * it says so rather than claiming nothing changed (UX review, 17 Oct 2026).
 * Anything else failed and can be tried again.
 */
export function voteFailure(error: unknown, vote = true): { kind: 'quiet' } | { kind: 'refused' | 'unconfirmed' | 'failed'; message: string } {
  if (error instanceof IntakeApiError && error.status === 404) return { kind: 'refused', message: VOTE_REFUSED_GONE_MESSAGE };
  if (error instanceof IntakeApiError && error.status === 409) {
    const message = error.code ? VOTE_REFUSALS.get(error.code) : undefined;
    return message ? { kind: 'refused', message } : { kind: 'quiet' };
  }
  if (error instanceof IntakeApiError && error.status === 0) {
    return { kind: 'unconfirmed', message: vote ? VOTE_UNCONFIRMED_MESSAGE : UNVOTE_UNCONFIRMED_MESSAGE };
  }
  return { kind: 'failed', message: VOTE_FAILED_MESSAGE };
}

/**
 * Vote (`true`) or take the vote back (`false`) on one item. No retry. The
 * reply's count replaces the count on every loaded feed page; the item read is
 * marked stale so it shows the same number (D-144), and read again if the idea
 * page has it open, so its list of names includes the voter (ATLAS-103b). A
 * refused, quiet or unconfirmed failure reads the feed and the item again. The card keeps its own optimistic guess
 * (`VoteButton`'s `useVoteState`), so a failure rolls back there.
 */
export function useVote(code: string) {
  const queryClient = useQueryClient();
  return useMutation<IntakeNeedResult, Error, boolean>({
    mutationFn: (vote) => (vote ? voteNeed(code) : unvoteNeed(code)),
    retry: false,
    onSuccess: async (result) => {
      queryClient.setQueriesData<InfiniteData<IntakeFeedRead, string | null>>({ queryKey: feedKeys.all }, (data) =>
        data
          ? { ...data, pages: data.pages.map((p) => ({ ...p, items: p.items.map((i) => (i.code === code ? { ...i, ...result } : i)) })) }
          : data,
      );
      await queryClient.invalidateQueries({ queryKey: intakeKeys.item(code) });
    },
    onError: async (error, vote) => {
      if (voteFailure(error, vote).kind !== 'failed') {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: feedKeys.all }),
          queryClient.invalidateQueries({ queryKey: intakeKeys.item(code) }),
        ]);
      }
    },
  });
}

// ── The idea page (ATLAS-103, U38) ───────────────────────────────────────────

/**
 * Answer one review team question. No retry; a failure shows beside the box,
 * not in the shared banner (D-169: failures show where the person acted).
 * The item is read again on success, so the question shows as answered.
 */
export function useAnswerReviewerQuestion(code: string) {
  return useIntakeMutation(
    ({ questionId, answer, idempotencyKey }: { questionId: string; answer: string; idempotencyKey: string }) =>
      answerReviewerQuestion(code, questionId, answer, { idempotencyKey }),
    { invalidate: [intakeKeys.item(code)], reportErrors: false },
  );
}

/**
 * The sender's "Make this private" (ATLAS-85's route). An "already private"
 * answer means an earlier try got there, so it counts as done (D-140). Either
 * way the item, the feed and the board are read again, and the status strip says it's private.
 */
export function useMakePrivate(code: string) {
  return useIntakeMutation(
    async (reason: string) => {
      try {
        return await makePrivate(code, reason);
      } catch (error) {
        if (error instanceof IntakeApiError && error.status === 409 && error.code === INTAKE_ALREADY_PRIVATE) return null;
        throw error;
      }
    },
    // The feed and the board too, so the sender's own card shows private at once (review UAT, ATLAS-103).
    { invalidate: [intakeKeys.item(code), feedKeys.all, ['intake', 'column']], reportErrors: false },
  );
}

/**
 * Stop (`false`) or restart (`true`) this idea's emails, on ATLAS-77's follow
 * route. No retry; a failure shows beside the button, not in the shared
 * banner (D-169). The item is read again, so the panel shows what's stored.
 */
export function useSetFollow(code: string) {
  return useIntakeMutation((follow: boolean) => setFollow(code, follow), { invalidate: [intakeKeys.item(code)], reportErrors: false });
}

// ── The review team's and admins' panels (ATLAS-104, U39) ───────────────────

export const reviewKey = ['intake', 'review'] as const;

/** The board, the feed and the weekly pass: what a decision, a check or an admin's choice moves. */
const LISTS: QueryKey[] = [['intake', 'column'], feedKeys.all, reviewKey];

export const TEAM_LOST_RIGHTS_MESSAGE = "You can't do this any more. Reload the page to see what you can do.";
export const TEAM_UNCONFIRMED_MESSAGE = "We couldn't confirm it was saved. Check the page before you try again.";

/**
 * What a failed team action says, beside the control that failed (D-169),
 * never in the shared banner. A timeout may have saved, so it says it
 * couldn't confirm rather than "nothing changed". The API's 403 and 404 are
 * the real guard; the panels only say what they mean.
 */
export function teamActionMessage(error: unknown, code: string): string {
  if (!(error instanceof IntakeApiError)) return "That didn't work. Try again.";
  if (error.status === 0) return TEAM_UNCONFIRMED_MESSAGE;
  if (error.status === 403) return TEAM_LOST_RIGHTS_MESSAGE;
  if (error.status === 404) return `${code} isn't there any more, or you can't see it.`;
  if (error.status === 409 && error.code === 'INTAKE_ITEM_CHANGED') return `Someone else changed ${code}. Reload the page to see their change.`;
  return error.message;
}

/**
 * One team action on one item. No retry and no shared banner. On success the
 * item, and any lists given, are read again. A 404 reads the item again too,
 * so the page shows "can't see it" (D-58); `refetchOn409` does the same for a
 * panel whose form should show what the other person saved.
 */
function useTeamAction<TVariables, TData>(
  code: string,
  fn: (variables: TVariables) => Promise<TData>,
  { lists = false, refetchOn409 = false }: { lists?: boolean; refetchOn409?: boolean } = {},
) {
  const queryClient = useQueryClient();
  return useMutation<TData, Error, TVariables>({
    mutationFn: fn,
    retry: false,
    onSuccess: async () => {
      await Promise.all([intakeKeys.item(code), ...(lists ? LISTS : [])].map((queryKey) => queryClient.invalidateQueries({ queryKey })));
    },
    onError: async (error) => {
      const status = error instanceof IntakeApiError ? error.status : 0;
      if (status === 404 || (status === 409 && refetchOn409)) await queryClient.invalidateQueries({ queryKey: intakeKeys.item(code) });
    },
  });
}

/**
 * The route triage suggested for this item and nobody has confirmed, or null.
 * From the item read's team-only `triage.view.suggestedRoute` (ATLAS-104b,
 * D-375), under the same conditions as before: a public item in Submitted
 * that waits on Product, for someone on the team. A plain function of the
 * item: no query, so the page no longer reads the weekly pass (D-177).
 * Confirming a route moves the item out of Submitted, so a confirmed route
 * never shows here.
 */
export function useRouteToConfirm(item: IntakeItemDetail, onTeam: boolean): { team: string; how: string } | null {
  const couldBeOpen = onTeam && !item.private && item.status === 'submitted' && item.waitingOn === 'product';
  if (!couldBeOpen) return null;
  return item.triage?.view?.suggestedRoute ?? null;
}

/**
 * The weekly pass for the review page (ATLAS-105). Sent only for a reviewer
 * or an admin: anyone else never asks (plan c1). The API's 403 is the guard.
 */
export function useWeeklyPass() {
  const access = useIntakeAccess();
  const onTeam = access.state === 'ready' && (access.role === 'reviewer' || access.role === 'admin');
  return useQuery<IntakeWeeklyPass, IntakeApiError>({
    queryKey: reviewKey,
    queryFn: fetchWeeklyPass,
    enabled: onTeam,
    retry: intakeReadRetry,
  });
}

/**
 * An admin sets up the next monthly review (ATLAS-105b, plan Q5). No retry and
 * no shared banner: the form says what failed beside itself (D-169). The
 * review is read again on success, and after a timeout, which may have saved.
 */
export function useCreateReviewCycle() {
  const queryClient = useQueryClient();
  return useMutation<IntakeReviewCycle, Error, IntakeReviewCycleInput>({
    mutationFn: createReviewCycle,
    retry: false,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: reviewKey });
    },
    onError: async (error) => {
      if (error instanceof IntakeApiError && error.status === 0) await queryClient.invalidateQueries({ queryKey: reviewKey });
    },
  });
}

export function useConfirmRoute(code: string) {
  return useTeamAction(code, (confirm: boolean) => confirmRoute(code, confirm), { lists: true, refetchOn409: true });
}

export function useAskSender(code: string) {
  return useTeamAction(code, (text: string) => askSender(code, text));
}

export function useEditItem(code: string) {
  return useTeamAction(code, (input: IntakeEditInput) => editItem(code, input));
}

export function useSetPriority(code: string) {
  return useTeamAction(code, (input: IntakePriorityInput) => setPriority(code, input), { lists: true, refetchOn409: true });
}

/** A decision. DecidePanel handles a 409 itself: it closes the form and reads the item again (D-172). */
export function useDecideItem(code: string) {
  return useTeamAction(code, (input: IntakeDecisionInput) => decideItem(code, input), { lists: true, refetchOn409: true });
}

export function useResolvePrivate(code: string) {
  return useTeamAction(code, (input: IntakeSensitiveResolveInput) => resolvePrivate(code, input), { lists: true, refetchOn409: true });
}

/**
 * An admin's "mark checked" (ATLAS-86, D-132, D-168). "Already checked" means
 * a second press or another admin got there: it counts as done, and the item
 * is read again either way.
 */
export function useMarkChecked(code: string) {
  return useTeamAction(
    code,
    async (input: IntakeCheckMarkInput) => {
      try {
        return await markChecked(code, input);
      } catch (error) {
        if (error instanceof IntakeApiError && error.status === 409 && error.code === 'INTAKE_ALREADY_CHECKED') return null;
        throw error;
      }
    },
    { lists: true, refetchOn409: true },
  );
}

/**
 * The review team's make-private (ATLAS-85). "Already private" counts as done
 * (D-140). When the reply says the caller can no longer see it, the item is
 * not read again (it would be a 404, D-58): the page stops reading it and
 * drops it from the cache (IntakeDetailPage's `leftView`). The lists are read
 * again either way, so it leaves the board.
 */
export function useTeamMakePrivate(code: string) {
  const queryClient = useQueryClient();
  return useMutation<IntakeMakePrivateResult | null, Error, string>({
    mutationFn: async (reason) => {
      try {
        return await makePrivate(code, reason);
      } catch (error) {
        if (error instanceof IntakeApiError && error.status === 409 && error.code === INTAKE_ALREADY_PRIVATE) return null;
        throw error;
      }
    },
    retry: false,
    onSuccess: async (result) => {
      const stillSeen = !result || result.visibleToYou;
      await Promise.all([...(stillSeen ? [intakeKeys.item(code)] : []), ...LISTS].map((queryKey) => queryClient.invalidateQueries({ queryKey })));
    },
  });
}

// ── The sender's own edit and withdraw (ATLAS-125, U46) ──────────────────────

/**
 * The sender's edit or withdraw. No retry and no shared banner: the section
 * says what failed beside the control (D-169). The idea and the lists are read
 * again on success, and after a timeout or a 404 or 409, any of which may mean
 * the idea changed under the page.
 */
function useSenderAction<TVariables>(code: string, fn: (variables: TVariables) => Promise<unknown>) {
  const queryClient = useQueryClient();
  const readAgain = () => Promise.all([intakeKeys.item(code), ...LISTS].map((queryKey) => queryClient.invalidateQueries({ queryKey })));
  return useMutation<unknown, Error, TVariables>({
    mutationFn: fn,
    retry: false,
    onSuccess: async () => {
      await readAgain();
    },
    onError: async (error) => {
      const status = error instanceof IntakeApiError ? error.status : 0;
      if (status === 0 || status === 404 || status === 409) await readAgain();
    },
  });
}

export function useEditOwnIdea(code: string) {
  return useSenderAction(code, ({ input, idempotencyKey }: { input: IntakeSenderEditInput; idempotencyKey: string }) =>
    editOwnIdea(code, input, { idempotencyKey }));
}

export function useWithdrawOwnIdea(code: string) {
  return useSenderAction(code, (idempotencyKey: string) => withdrawOwnIdea(code, { idempotencyKey }));
}
